import axios from "axios";

import {
  Client,
  Comment,
  RegisterRequest,
  User,
  UserResponse,
  PasswordChangeRequest,
} from "./types";

import { namespace } from "./environment";

export const fetchClients = async (jwtToken: string): Promise<Client[]> => {
  try {
    const response = await axios.get(`${namespace}/clients`, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${jwtToken}`
      }
    });
    const data = response.data;
    if (!Array.isArray(data)) {
      throw new Error("Invalid response: expected an array");
    }
    return data as Client[];
  } catch (error) {
    console.error(
      `Failed to fetch clients from ${namespace}/clients:`,
      error
    );
    throw error;
  }
};

export const saveClient = async (
  clientData: Partial<Client>,
  jwtToken: string
): Promise<Client> => {
  try {
    const response = await axios.post<Client>(
      `${namespace}/clients`,
      clientData,
      {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${jwtToken}`
        }
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error adding client:", error);
    throw error;
  }
};

export const deleteClient = async (clientId: string, jwtToken: string) => {
  try {
    const response = await axios.delete<Client>(
      `${namespace}/clients/${clientId}`,
      {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${jwtToken}`
        }
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error deleting client:", error);
    throw error;
  }
};

export const updateClient = async (clientId: string, client: any, jwtToken: string) => {
  try {
    const response = await axios.patch<Client>(
      `${namespace}/clients/${clientId}`,
      client,
      {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${jwtToken}`
        }
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error updating client:", error);
    throw error;
  }
};

export const saveClientNote = async (clientId: string, note: Comment, jwtToken: string) => {
  try {
    const response = await axios.post<Comment>(
      `${namespace}/client-note/client/${clientId}`,
      note,
      {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${jwtToken}`
        }
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error adding note:", error);
    throw error;
  }
};

export const editClientNote = async (clientId: string, note: Comment, jwtToken: string) => {
  try {
    const response = await axios.patch<Comment>(
      `${namespace}/client-note/${clientId}`,
      note,
      {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${jwtToken}`
        }
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error editing note:", error);
    throw error;
  }
};

export const deleteClientNote = async (clientId: string, jwtToken: string) => {
  try {
    const response = await axios.delete<Comment>(
      `${namespace}/client-note/${clientId}`,
      {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${jwtToken}`
        }
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error deleting note:", error);
    throw error;
  }
};

export const login = async (user: User, jwtToken: string) => {
  try {
    const response = await axios.post<UserResponse>(
      `${namespace}/users/login`,
      user,
      {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${jwtToken}`
        }
      }
    );
    const data = response.data;
    return {
      userId: data.userId,
      role: data.role,
      email: data.email,
      username: data.username,
      jwtToken: data.jwtToken,
    };
  } catch (error) {
    console.error("Signing In Error:", error);
    throw error;
  }
};

export const register = async (registeredUser: RegisterRequest, jwtToken: string) => {
  try {
    const response = await axios.post(
      `${namespace}/users`,
      registeredUser,
      {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${jwtToken}`
        }
      }
    );
    return response.data;
  } catch (error) {
    console.error("Register Error:", error);
    throw error;
  }
};

export const updateUser = async (id: string, user: any, jwtToken: string) => {
  try {
    const response = await axios.patch(
      `${namespace}/users/${id}`,
      user,
      {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${jwtToken}`
        }
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error:", error);
    throw error;
  }
};

export const updatePassword = async (
  id: string,
  passwordResetRequest: PasswordChangeRequest,
  jwtToken: string
) => {
  try {
    const response = await axios.patch(
      `${namespace}/users/${id}/password`,
      passwordResetRequest,
      {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${jwtToken}`
        }
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error:", error);
    throw error;
  }
};